// Tiny History API router with support for :params. Components navigate via
// router.navigate(); the router notifies subscribers on every change.

export type RouteName =
  | 'login'
  | 'forgot'
  | 'reset'
  | 'setup'
  | 'dashboard'
  | 'students'
  | 'student-detail'
  | 'teachers'
  | 'teacher-detail'
  | 'families'
  | 'accounting'
  | 'reporting'
  | 'classes'
  | 'class-detail'
  | 'events'
  | 'event-detail'
  | 'mailing'
  | 'media'
  | 'irish-dance'
  | 'audit'
  | 'settings'
  | 'onboarding'
  | 'portal-login'
  | 'portal-dashboard'
  | 'portal-classes'
  | 'portal-levels'
  | 'portal-payments'
  | 'portal-calendar'
  | 'portal-events'
  | 'portal-event-detail'
  | 'portal-profile'
  | 'portal-contract'
  | 'portal-gallery'
  | 'not-found';

export interface RouteMatch {
  path: string;
  name: RouteName;
  params: Record<string, string>;
  isAdmin: boolean;
  isPortal: boolean;
}

interface RouteDef { pattern: string; name: RouteName; regex: RegExp; keys: string[]; }

function compile(pattern: string, name: RouteName): RouteDef {
  const keys: string[] = [];
  const regex = new RegExp(
    '^' + pattern.replace(/:[^/]+/g, (m) => { keys.push(m.slice(1)); return '([^/]+)'; }) + '$',
  );
  return { pattern, name, regex, keys };
}

const ROUTES: RouteDef[] = [
  compile('/login', 'login'),
  compile('/forgot-password', 'forgot'),
  compile('/reset-password', 'reset'),
  compile('/setup', 'setup'),
  compile('/admin/dashboard', 'dashboard'),
  compile('/admin/students', 'students'),
  compile('/admin/students/:id', 'student-detail'),
  compile('/admin/teachers', 'teachers'),
  compile('/admin/teachers/:id', 'teacher-detail'),
  compile('/admin/families', 'families'),
  compile('/admin/accounting', 'accounting'),
  compile('/admin/reporting', 'reporting'),
  compile('/admin/classes', 'classes'),
  compile('/admin/classes/:id', 'class-detail'),
  compile('/admin/events', 'events'),
  compile('/admin/events/:id', 'event-detail'),
  compile('/admin/mailing', 'mailing'),
  compile('/admin/media', 'media'),
  compile('/admin/irish-dance', 'irish-dance'),
  compile('/admin/audit', 'audit'),
  compile('/admin/settings', 'settings'),
  compile('/admin/settings/:section', 'settings'),
  compile('/onboarding/:token', 'onboarding'),
  compile('/portal', 'portal-login'),
  compile('/portal/login', 'portal-login'),
  compile('/portal/dashboard', 'portal-dashboard'),
  compile('/portal/classes', 'portal-classes'),
  compile('/portal/levels', 'portal-levels'),
  compile('/portal/payments', 'portal-payments'),
  compile('/portal/calendar', 'portal-calendar'),
  compile('/portal/events', 'portal-events'),
  compile('/portal/events/:id', 'portal-event-detail'),
  compile('/portal/profile', 'portal-profile'),
  compile('/portal/contract', 'portal-contract'),
  compile('/portal/gallery', 'portal-gallery'),
];

type Listener = (match: RouteMatch) => void;

class Router {
  private listeners = new Set<Listener>();

  match(path = window.location.pathname): RouteMatch {
    for (const route of ROUTES) {
      const m = route.regex.exec(path);
      if (m) {
        const params: Record<string, string> = {};
        route.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
        return { path, name: route.name, params, isAdmin: path.startsWith('/admin'), isPortal: path.startsWith('/portal') };
      }
    }
    return { path, name: 'not-found', params: {}, isAdmin: path.startsWith('/admin'), isPortal: path.startsWith('/portal') };
  }

  current(): RouteMatch { return this.match(); }

  navigate(path: string, opts: { replace?: boolean } = {}): void {
    if (path === window.location.pathname) return;
    if (opts.replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
    this.emit();
  }

  redirect(path: string): void {
    if (path === window.location.pathname) { this.emit(); return; }
    window.history.replaceState({}, '', path);
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start(): void {
    window.addEventListener('popstate', () => this.emit());
    document.addEventListener('click', (e) => {
      const anchor = e.composedPath().find(
        (el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && el.hasAttribute('data-link'),
      );
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || anchor.target === '_blank') return;
      e.preventDefault();
      this.navigate(href);
    });
  }

  private emit(): void {
    const match = this.current();
    for (const listener of this.listeners) listener(match);
  }
}

export const router = new Router();
